// src/components/Atelier4/CTIFilteringInfo.js
import React from 'react';
import { Info, Filter, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { getFilteringSummary, STATUS_LABELS } from '../../utils/ctiFilters';

/**
 * Component to display CTI filtering information and statistics
 */
const CTIFilteringInfo = ({ filterMetadata, showDetails = true, className = '' }) => {
  if (!filterMetadata) return null;

  const summary = getFilteringSummary(filterMetadata);
  if (!summary) return null;

  const { summary: stats, statuses } = summary;

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <Filter className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">
            Filtrage des données CTI pour génération de scénarios
          </h4>
          
          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            {/* Total */}
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Total</span>
                <span className="text-xs text-blue-600">{stats.total.percentage}% conservé</span>
              </div>
              <div className="mt-1">
                <span className="text-lg font-bold text-blue-900">{stats.total.filtered}</span>
                <span className="text-sm text-gray-500">/{stats.total.original}</span>
              </div>
              {stats.total.excluded > 0 && (
                <div className="text-xs text-red-600 mt-1">
                  {stats.total.excluded} élément(s) exclu(s)
                </div>
              )}
            </div>

            {/* Vulnerabilities */}
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Vulnérabilités</span>
                <span className="text-xs text-orange-600">{stats.vulnerabilities.percentage}% conservé</span>
              </div>
              <div className="mt-1">
                <span className="text-lg font-bold text-orange-900">{stats.vulnerabilities.filtered}</span>
                <span className="text-sm text-gray-500">/{stats.vulnerabilities.original}</span>
              </div>
              {stats.vulnerabilities.excluded > 0 && (
                <div className="text-xs text-red-600 mt-1">
                  {stats.vulnerabilities.excluded} exclue(s)
                </div>
              )}
            </div>

            {/* Techniques */}
            <div className="bg-white rounded-lg p-3 border border-blue-100">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">Techniques</span>
                <span className="text-xs text-purple-600">{stats.techniques.percentage}% conservé</span>
              </div>
              <div className="mt-1">
                <span className="text-lg font-bold text-purple-900">{stats.techniques.filtered}</span>
                <span className="text-sm text-gray-500">/{stats.techniques.original}</span>
              </div>
              {stats.techniques.excluded > 0 && (
                <div className="text-xs text-red-600 mt-1">
                  {stats.techniques.excluded} exclue(s)
                </div>
              )}
            </div>
          </div>

          {showDetails && (
            <>
              {/* Status Information */}
              <div className="border-t border-blue-200 pt-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Included Statuses */}
                  <div>
                    <h5 className="text-xs font-semibold text-green-800 mb-2 flex items-center">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Statuts inclus dans les scénarios
                    </h5>
                    <div className="space-y-1">
                      {statuses.allowed.map(status => (
                        <div key={status.id} className="flex items-center text-xs">
                          <div className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(status.id)}`}></div>
                          <span className="text-gray-700">{status.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Excluded Statuses */}
                  {statuses.excluded.length > 0 && (
                    <div>
                      <h5 className="text-xs font-semibold text-red-800 mb-2 flex items-center">
                        <XCircle className="h-3 w-3 mr-1" />
                        Statuts exclus des scénarios
                      </h5>
                      <div className="space-y-1">
                        {statuses.excluded.map(status => (
                          <div key={status.id} className="flex items-center text-xs">
                            <div className={`w-2 h-2 rounded-full mr-2 ${getStatusColor(status.id)}`}></div>
                            <span className="text-gray-700">{status.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Information Note */}
              <div className="border-t border-blue-200 pt-3 mt-3">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-800">
                    <p className="mb-1">
                      <strong>Logique de filtrage :</strong> Seules les vulnérabilités et techniques avec un statut 
                      "Non Corrigé" ou "Accepté" sont considérées pour la génération de scénarios opérationnels.
                    </p>
                    <p>
                      Les éléments "Corrigés" sont exclus car ils ne représentent plus un risque actuel 
                      pour l'organisation.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Compact version of the filtering info for inline display
 */
export const CTIFilteringInfoCompact = ({ filterMetadata, className = '' }) => {
  if (!filterMetadata) return null;

  const summary = getFilteringSummary(filterMetadata);
  if (!summary) return null;

  const { summary: stats } = summary;

  return (
    <div className={`inline-flex items-center space-x-2 text-xs ${className}`}>
      <Filter className="h-3 w-3 text-blue-600" />
      <span className="text-gray-600">
        Données filtrées:
      </span>
      <span className="font-medium text-blue-900">
        {stats.total.filtered}/{stats.total.original}
      </span>
      <span className="text-gray-500">
        ({stats.total.percentage}% conservé)
      </span>
      {stats.total.excluded > 0 && (
        <span className="text-red-600">
          • {stats.total.excluded} exclu(s)
        </span>
      )}
    </div>
  );
};

/**
 * Warning component when filtered data is insufficient
 */
export const CTIFilteringWarning = ({ validationResult, className = '' }) => {
  if (!validationResult || validationResult.isValid) return null;

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-yellow-900 mb-1">
            Données CTI insuffisantes après filtrage
          </h4>
          <p className="text-sm text-yellow-800 mb-2">
            {validationResult.message}
          </p>
          <div className="text-xs text-yellow-700">
            <p>
              <strong>Suggestions :</strong>
            </p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Vérifiez les statuts des vulnérabilités et techniques dans l'analyse CTI</li>
              <li>Modifiez le statut des éléments pertinents de "Corrigé" vers "Non Corrigé" ou "Accepté"</li>
              <li>Effectuez une nouvelle analyse CTI pour obtenir plus de données</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get status color classes
const getStatusColor = (status) => {
  const colorMap = {
    'non_corrige': 'bg-red-500',
    'corrige': 'bg-green-500',
    'accepté': 'bg-yellow-500',
    'risque_accepte': 'bg-yellow-500'
  };
  return colorMap[status] || 'bg-gray-500';
};

export default CTIFilteringInfo;
